import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Paper, Container } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLoader } from '../../../lib/hooks';
import useGameService from '../../../services/GameService';
import Details from '../../../components/public/game/Game/Details';
import PlayerList from '../../../components/public/game/Game/PlayerList';
import SttError from '../../../components/error/SttError';
import Actions from '../../../components/public/game/Game/Actions';
import SignUpDialog from '../../../components/public/game/Game/SignUpDialog';
import Header from '../../../components/public/game/Game/Header';
import { Player } from '../../../types/models/Player';
import { AppContext } from '../../../sporttia/AppContext';
import useScsService from '../../../services/ScsService';
import {
    canAddMorePlayersToGame,
    wasCreatedWithFullRateForced,
} from '../../../lib/GameUtils';

export default function Game() {
    const { id } = useParams<{ id: string }>();
    const cxt = useContext(AppContext)!;

    const { useGetGame, useDeleteGamePlayers } = useGameService();
    const { useGetScMe } = useScsService();

    const [openSignupDialog, setOpenSignupDialog] = useState(false);
    const [imUserPrivileged, setImUserPrivileged] = useState(false);
    const [players, setPlayers] = useState<Player[]>([]);
    const [deletingPlayerId, setDeletingPlayerId] = useState<number | null>(
        null,
    );
    const [emailSent, setEmailSent] = useState(false);
    const { t } = useTranslation();

    const signupParamsCode = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('code');
    }, []);

    const {
        data: gameResponse,
        status: gameStatus,
        isError,
    } = useGetGame(parseInt(id || '0'));

    const { game } = useMemo(
        () => ({ game: gameResponse?.game }),
        [gameResponse],
    );

    const { status: mshipStatus, data: mshipData } = useGetScMe(
        game?.facility?.sc?.id ?? -1,
        {},
        {
            enabled:
                cxt.user && cxt.user?.role === 'USER' && game !== undefined,
        } as any,
    );

    const [, loader] = useLoader([gameStatus, mshipStatus]);

    useEffect(() => {
        if (game && Array.isArray(game.players)) {
            setPlayers(game.players);
        }
    }, [game]);

    // Handle mship privileges
    useEffect(() => {
        if (mshipData?.mship && mshipData.mship.privileges?.includes(19)) {
            setImUserPrivileged(true);
        }
    }, [mshipData]);

    const deletePlayerMutation = useDeleteGamePlayers({
        onSuccess: (_: any, variables: any) => {
            setPlayers((prev: any) =>
                prev.filter((p: any) => p.id !== variables.id),
            );
            setDeletingPlayerId(null);
        },
        onError: (err: unknown) => {
            setDeletingPlayerId(null);
            const apiErr = err as any;
            const errorMsg =
                apiErr?.error?.msg || t('Error al eliminar el jugador');
            cxt.showMessage('E', errorMsg);
        },
    });

    const handleDeletePlayer = (player: Player) => {
        setDeletingPlayerId(player.id);
        deletePlayerMutation.mutate({
            id: player.id,
            params: { cancelGameOnLastPlayer: true },
        });
    };

    const canDeletePlayer = (player: Player) => player.user.id === cxt.user?.id;

    const facilityCapacity = game?.facility.schedule?.capacity || 0;

    if (isError) return <SttError />;
    if (!game) return null;

    const slots = Array(facilityCapacity)
        .fill(null)
        .map((_, i) => players[i] || null);

    const creator = game?.creator || null;

    return (
        <Box style={{ minHeight: '100vh', padding: 24 }}>
            {loader}
            {((game && game.signupCode === signupParamsCode) ||
                imUserPrivileged) && (
                <Container maxWidth="md">
                    <Header isPrivate={game.signupCode !== null} />

                    {/* Indicador de tarifa completa forzada */}
                    {wasCreatedWithFullRateForced(game) && (
                        <Box
                            style={{
                                marginBottom: 16,
                                padding: '12px 16px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: 4,
                                border: '1px solid #90caf9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <InfoOutlined
                                style={{ color: '#1976d2', fontSize: 20 }}
                            />
                            <Box style={{ color: '#1565c0', fontSize: 14 }}>
                                {t('game.createdWithFullRateWarning')}
                            </Box>
                        </Box>
                    )}

                    <Paper style={{ marginBottom: 24, padding: 16 }}>
                        <Details game={game} />
                    </Paper>
                    <Paper style={{ marginBottom: 24, padding: 16 }}>
                        <PlayerList
                            slots={slots}
                            onDeletePlayer={handleDeletePlayer}
                            canDeletePlayer={canDeletePlayer}
                            deletingPlayerId={deletingPlayerId}
                            creator={creator}
                        />
                    </Paper>

                    {game.signupCode === signupParamsCode && (
                        <Actions
                            game={game}
                            onOpenSignupDialog={() => setOpenSignupDialog(true)}
                            emailSent={emailSent}
                            facilityCapacity={facilityCapacity ?? 0}
                            canAddMorePlayers={canAddMorePlayersToGame(game)}
                        />
                    )}

                    <SignUpDialog
                        open={openSignupDialog}
                        gameId={game.id}
                        onClose={() => setOpenSignupDialog(false)}
                        onEmailSent={() => setEmailSent(true)}
                    />
                </Container>
            )}
        </Box>
    );
}
